import {
    api,
    apiErrorMessage,
    useAuthStore,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    DateTime,
    Field,
    FormSheet,
    Input,
    PageHeader,
    RowActions,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { RiskBacktest, RiskCurve, RiskProfile } from '@/api/types';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { BacktestResultsView, CurveChart } from '@/pages/risk/learning';
import { PublishDialog } from '@/pages/risk/rules';

const PATH = '/staff/risk/profiles';
const APPETITES = ['strict', 'balanced', 'growth'] as const;

/** Slice 2 (decisions 25, 27, 28): the review and graph params; empty inherits the parent's value. */
const PARAMS = [
    [
        'review_sla_hours',
        'Review due within (hours)',
        'A case still open after this is late: it sorts first and raises an alert',
    ],
    [
        'case_expiry_hours',
        'Void undecided cases after (hours)',
        'Must stay under the payment hold limit',
    ],
    [
        'contamination_hop1',
        'Score from a flagged key one link away',
        '0 to 100',
    ],
    [
        'contamination_hop2',
        'Score from a flagged key two links away',
        '0 to 100',
    ],
    [
        'graph_degree_cap',
        'Ignore keys linked to more than',
        'A key with more links is shared (a carrier IP) and spreads nothing',
    ],
    // slice 5 (decision 13): the k of precision at k in backtests and model metrics
    [
        'queue_capacity_per_day',
        'Review cases the team works a day',
        'Backtests and models report precision among this many top scores per day',
    ],
] as const;

type ParamKey = (typeof PARAMS)[number][0];

type ProfileParams = Partial<Record<ParamKey, number | null>>;

/** What the PATCH sends: a typed number sets the param, an emptied one is null (inherited again), untouched ones are left out. */
export function paramsPatch(
    own: ProfileParams,
    typed: Partial<Record<ParamKey, string>>,
): ProfileParams {
    const out: ProfileParams = {};
    for (const [key] of PARAMS) {
        const v = (typed[key] ?? '').trim();
        const was = own[key] ?? null;
        if (v === '') {
            if (was !== null) out[key] = null;
        } else if (Number(v) !== was) out[key] = Number(v);
    }
    return out;
}

/** Decision 34's switch in the PATCH: on / off set it, inherit clears an own value, an unchanged choice is left out. */
export function storeSwitchPatch(
    own: boolean | null,
    choice: string,
): { store_action_pauses_merchant?: boolean | null } {
    const next = choice === 'inherit' ? null : choice === 'on';
    return next === own ? {} : { store_action_pauses_merchant: next };
}

type ProfileDetail = RiskProfile & {
    versions?: {
        version: number;
        published_at: string;
        published_by: string;
        reason?: string | null;
    }[];
    assignments?: { merchant_id: string | null; store_id: string | null }[];
    overrides?: Record<string, unknown>;
};

/** BM-125 decision 13: a profile's appetite picks the bands (3DS / review / decline) within the parent's allowed range. */
function ProfileSheet({
    profile,
    parent,
    onClose,
}: {
    profile: RiskProfile | null;
    parent: RiskProfile | null;
    onClose: () => void;
}) {
    const [scope, setScope] = useState<string>(
        profile?.scope ??
            (parent?.scope === 'platform' ? 'tenant' : 'merchant'),
    );
    const [name, setName] = useState(profile?.name ?? '');
    const [appetite, setAppetite] = useState<string>(
        profile?.appetite ?? 'balanced',
    );
    const [target, setTarget] = useState(
        profile?.merchant_id ?? profile?.store_id ?? '',
    );
    const [range, setRange] = useState<string[]>([
        ...(profile?.appetite_range ?? APPETITES),
    ]);
    const own = (profile?.params ?? {}) as ProfileParams;
    const effective = (profile?.effective_params ?? {}) as ProfileParams;
    const [params, setParams] = useState<Partial<Record<ParamKey, string>>>(
        () =>
            Object.fromEntries(
                PARAMS.map(([key]) => [
                    key,
                    own[key] == null ? '' : String(own[key]),
                ]),
            ),
    );
    // slice 3 (decision 34): a store's PT Action pauses the whole merchant unless a profile turns it off
    const ownStore = profile?.params?.store_action_pauses_merchant ?? null;
    const [storeSwitch, setStoreSwitch] = useState<string>(
        ownStore === null ? 'inherit' : ownStore ? 'on' : 'off',
    );
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(async () => {
        if (profile)
            return (
                await api().patch<RiskProfile>(`${PATH}/${profile.id}`, {
                    name,
                    appetite,
                    appetite_range: range,
                    params: {
                        ...paramsPatch(own, params),
                        ...storeSwitchPatch(ownStore, storeSwitch),
                    },
                })
            ).data;
        return (
            await api().post<RiskProfile>(PATH, {
                scope,
                name,
                appetite,
                ...(scope === 'merchant'
                    ? { merchant_id: target.trim() }
                    : scope === 'store'
                      ? { store_id: target.trim() }
                      : {}),
            })
        ).data;
    }, [PATH, '/staff/risk-rules']);
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title={profile ? `Edit ${profile.name}` : 'New profile'}
            submitLabel={profile ? 'Save changes' : 'Create profile'}
            busy={write.isPending}
            submitDisabled={name.trim() === ''}
            dirty
            error={error}
            data-testid="risk-profile-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            {profile ? null : (
                <Field id="prf-scope" label="Scope">
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger id="prf-scope">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {['tenant', 'merchant', 'store'].map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            )}
            <Field id="prf-name" label="Name">
                <Input
                    id="prf-name"
                    maxLength={80}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </Field>
            {!profile && scope !== 'tenant' ? (
                <Field
                    id="prf-target"
                    label={scope === 'merchant' ? 'Merchant' : 'Store'}
                    hint="The id the profile is assigned to"
                >
                    <Input
                        id="prf-target"
                        placeholder={scope === 'merchant' ? 'mer_…' : 'sto_…'}
                        value={target}
                        onChange={(e) => setTarget(e.target.value)}
                    />
                </Field>
            ) : null}
            <Field
                id="prf-appetite"
                label="Appetite"
                hint={
                    parent
                        ? `The parent allows ${parent.appetite_range.join(', ')}`
                        : 'strict 20/40/80, balanced 30/50/100, growth 40/60/100'
                }
            >
                <Select value={appetite} onValueChange={setAppetite}>
                    <SelectTrigger id="prf-appetite">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {APPETITES.map((a) => (
                            <SelectItem key={a} value={a}>
                                {a}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            {profile ? (
                <Field
                    id="prf-range"
                    label="Children may pick"
                    hint="The appetites a child profile may dial to"
                >
                    <div className="flex flex-wrap gap-2" id="prf-range">
                        {APPETITES.map((a) => (
                            <Button
                                key={a}
                                type="button"
                                size="sm"
                                variant={
                                    range.includes(a) ? 'default' : 'outline'
                                }
                                aria-pressed={range.includes(a)}
                                onClick={() =>
                                    setRange(
                                        range.includes(a)
                                            ? range.filter((r) => r !== a)
                                            : [...range, a],
                                    )
                                }
                            >
                                {a}
                            </Button>
                        ))}
                    </div>
                </Field>
            ) : null}
            {profile
                ? PARAMS.map(([key, label, hint]) => (
                      <Field
                          key={key}
                          id={`prf-${key}`}
                          label={label}
                          hint={`${hint}. Empty inherits ${effective[key] ?? '—'}.`}
                      >
                          <Input
                              id={`prf-${key}`}
                              inputMode="numeric"
                              placeholder={
                                  effective[key] == null
                                      ? ''
                                      : String(effective[key])
                              }
                              value={params[key] ?? ''}
                              onChange={(e) =>
                                  setParams({
                                      ...params,
                                      [key]: e.target.value.replace(
                                          /[^0-9]/g,
                                          '',
                                      ),
                                  })
                              }
                          />
                      </Field>
                  ))
                : null}
            {profile ? (
                <Field
                    id="prf-store-action"
                    label="A store at Action pauses the merchant"
                    hint={`A store crossing a monitoring rule's Action level pauses the whole merchant's payouts. Inherit uses ${profile.effective_params?.store_action_pauses_merchant === false ? 'off' : 'on'}.`}
                >
                    <Select value={storeSwitch} onValueChange={setStoreSwitch}>
                        <SelectTrigger id="prf-store-action">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="inherit">Inherit</SelectItem>
                            <SelectItem value="on">On</SelectItem>
                            <SelectItem value="off">Off</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
            ) : null}
        </FormSheet>
    );
}

/** BM-125 decision 13: the backtests of a profile and the appetite curve beside the dial. */
function LearningCard({ profile }: { profile: RiskProfile }) {
    const can = useAuthStore((s) => s.can);
    const [showCurve, setShowCurve] = useState(false);
    const [windowDays, setWindowDays] = useState<string>('30');
    const [selected, setSelected] = useState<string | null>(null);
    const backtests = useOne<RiskBacktest[]>(`${PATH}/${profile.id}/backtests`);
    const curve = useOne<RiskCurve>(
        showCurve ? `${PATH}/${profile.id}/curve` : null,
    );
    const run = useWrite(
        async () =>
            (
                await api().post<RiskBacktest>(
                    `${PATH}/${profile.id}/backtests`,
                    { window_days: Number(windowDays) },
                    { idempotencyKey: crypto.randomUUID() },
                )
            ).data,
        [`${PATH}/${profile.id}/backtests`],
    );
    const rows = backtests.data?.data ?? [];
    const current = rows.find((b) => b.id === selected) ?? rows[0] ?? null;
    // a queued or running backtest is read again every few seconds until it is done or failed
    const client = useQueryClient();
    const pending = rows.some(
        (b) => b.state === 'queued' || b.state === 'running',
    );
    useEffect(() => {
        if (!pending) return undefined;
        const timer = window.setInterval(() => {
            void client.invalidateQueries({
                predicate: (q) =>
                    JSON.stringify(q.queryKey).includes(
                        `${PATH}/${profile.id}/backtests`,
                    ),
            });
        }, 3000);
        return () => window.clearInterval(timer);
    }, [pending, client, profile.id]);
    return (
        <div className="space-y-3" data-testid="risk-learning">
            <div className="flex flex-wrap items-center gap-2">
                <Field id={`bt-window-${profile.id}`} label="Backtest window">
                    <Select value={windowDays} onValueChange={setWindowDays}>
                        <SelectTrigger id={`bt-window-${profile.id}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="30">Last 30 days</SelectItem>
                            <SelectItem value="90">Last 90 days</SelectItem>
                        </SelectContent>
                    </Select>
                </Field>
                {can(P.riskProfilesManage) ? (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={run.isPending}
                        onClick={() =>
                            run.mutate(undefined, {
                                onSuccess: (b) => setSelected(b.id),
                            })
                        }
                    >
                        Run backtest on the draft
                    </Button>
                ) : null}
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCurve(!showCurve)}
                >
                    {showCurve ? 'Hide appetite curve' : 'Show appetite curve'}
                </Button>
            </div>
            {run.error ? (
                <p className="text-destructive m-0 text-sm">
                    {apiErrorMessage(run.error)}
                </p>
            ) : null}
            {rows.length ? (
                <div className="overflow-x-auto">
                    <Table data-testid="risk-backtests">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Run</TableHead>
                                <TableHead>Window</TableHead>
                                <TableHead>State</TableHead>
                                <TableHead>By</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rows.map((b) => (
                                <TableRow
                                    key={b.id}
                                    data-selected={String(b.id === current?.id)}
                                >
                                    <TableCell>
                                        <DateTime value={b.created_at} />
                                    </TableCell>
                                    <TableCell>{b.window_days} days</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                b.state === 'done'
                                                    ? 'success'
                                                    : b.state === 'failed'
                                                      ? 'destructive'
                                                      : 'outline'
                                            }
                                        >
                                            {b.state === 'running'
                                                ? `${b.progress}%`
                                                : b.state}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{b.run_by}</TableCell>
                                    <TableCell>
                                        {b.id !== current?.id ? (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() =>
                                                    setSelected(b.id)
                                                }
                                            >
                                                Show
                                            </Button>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : null}
            {current?.results ? (
                <BacktestResultsView results={current.results} />
            ) : null}
            {current && current.state === 'failed' ? (
                <p className="text-destructive m-0 text-sm">{current.error}</p>
            ) : null}
            {showCurve ? (
                curve.data ? (
                    <CurveChart curve={curve.data.data} />
                ) : (
                    <p className="text-fg-2 m-0 text-sm">
                        Replaying the labelled checks under the three presets…
                    </p>
                )
            ) : null}
        </div>
    );
}

function ProfileCard({
    profile,
    all,
    onEdit,
    onPublish,
}: {
    profile: RiskProfile;
    all: RiskProfile[];
    onEdit: () => void;
    onPublish: () => void;
}) {
    const can = useAuthStore((s) => s.can);
    const platformHost = useIsPlatformHost();
    const detail = useOne<ProfileDetail>(`${PATH}/${profile.id}`);
    const d = detail.data?.data;
    const [rollback, setRollback] = useState<number | null>(null);
    const roll = useWrite(
        async (version: number) =>
            (await api().post(`${PATH}/${profile.id}/rollback`, { version }))
                .data,
        [PATH, '/staff/risk-rules'],
    );
    const editable = platformHost
        ? profile.scope === 'platform'
        : profile.scope !== 'platform';
    const parent =
        all.find((p) => p.id === (profile.parent_id ?? null)) ?? null;
    return (
        <Card
            data-testid={`risk-profile-${profile.scope}`}
            className="ml-0 md:ml-[calc(var(--depth)*1.5rem)]"
            style={{ '--depth': depthOf(profile, all) } as React.CSSProperties}
        >
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">
                    {profile.name}{' '}
                    <Badge variant="outline">{profile.scope}</Badge>{' '}
                    <span className="text-fg-2 text-sm font-normal">
                        v{profile.version} · {profile.appetite} · 3DS{' '}
                        {profile.bands.three_ds} / review {profile.bands.review}{' '}
                        / decline {profile.bands.decline}
                        {profile.effective_params
                            ? ` · review in ${profile.effective_params.review_sla_hours ?? '—'} h, void after ${profile.effective_params.case_expiry_hours ?? '—'} h`
                            : null}
                    </span>
                </CardTitle>
                <div className="flex flex-wrap gap-2">
                    {editable && can(P.riskProfilesManage) ? (
                        <Button size="sm" variant="outline" onClick={onEdit}>
                            Edit
                        </Button>
                    ) : null}
                    {editable && can(P.riskRulesManage) ? (
                        <Button size="sm" onClick={onPublish}>
                            Publish
                        </Button>
                    ) : null}
                </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                {parent ? (
                    <p className="text-fg-2 m-0">Inherits {parent.name}</p>
                ) : null}
                {profile.merchant_id || profile.store_id ? (
                    <p className="m-0">
                        Assigned to{' '}
                        <span className="font-mono text-xs">
                            {profile.merchant_id ?? profile.store_id}
                        </span>
                    </p>
                ) : null}
                {d?.overrides && Object.keys(d.overrides).length ? (
                    <p className="m-0">
                        Overrides: {Object.keys(d.overrides).join(', ')}
                    </p>
                ) : null}
                {editable ? <LearningCard profile={profile} /> : null}
                {d?.versions?.length ? (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Version</TableHead>
                                    <TableHead>Published</TableHead>
                                    <TableHead>By</TableHead>
                                    <TableHead>Reason</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {d.versions.map((v) => (
                                    <TableRow key={v.version}>
                                        <TableCell>{v.version}</TableCell>
                                        <TableCell>
                                            <DateTime value={v.published_at} />
                                        </TableCell>
                                        <TableCell>{v.published_by}</TableCell>
                                        <TableCell>{v.reason ?? '—'}</TableCell>
                                        <TableCell>
                                            {editable &&
                                            can(P.riskRulesManage) &&
                                            v.version !== profile.version ? (
                                                <RowActions
                                                    name={`version ${v.version}`}
                                                    items={[
                                                        {
                                                            label: 'Roll back',
                                                            onSelect: () =>
                                                                setRollback(
                                                                    v.version,
                                                                ),
                                                        },
                                                    ]}
                                                />
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <p className="text-fg-2 m-0">
                        Never published: the rows run as they are.
                    </p>
                )}
            </CardContent>
            {rollback !== null ? (
                <ConfirmDialog
                    open
                    onOpenChange={(o) => (o ? null : setRollback(null))}
                    title={`Roll back to version ${rollback}`}
                    // BM-158 self-review: the body is the dialog's <p> description, so it takes text, never a <p>
                    body="Publishes a new version with that version's overrides, bands and appetite."
                    confirmLabel="Roll back"
                    busy={roll.isPending}
                    error={roll.error ? apiErrorMessage(roll.error) : undefined}
                    onConfirm={() =>
                        roll.mutate(rollback, {
                            onSuccess: () => setRollback(null),
                        })
                    }
                />
            ) : null}
        </Card>
    );
}

function depthOf(p: RiskProfile, all: RiskProfile[]): number {
    let depth = 0;
    let cur: RiskProfile | undefined = p;
    while (cur?.parent_id) {
        depth++;
        cur = all.find((x) => x.id === cur?.parent_id);
        if (depth > 4) break;
    }
    return depth;
}

export function RiskProfilesPage() {
    const can = useAuthStore((s) => s.can);
    const platformHost = useIsPlatformHost();
    const tree = useOne<RiskProfile[]>(PATH);
    const all = tree.data?.data ?? [];
    const [editing, setEditing] = useState<RiskProfile | null | 'new'>(null);
    const [publishing, setPublishing] = useState<RiskProfile | null>(null);
    const ordered = [...all].sort(
        (a, b) =>
            depthOf(a, all) - depthOf(b, all) || a.name.localeCompare(b.name),
    );
    return (
        <div>
            <PageHeader
                title="Risk profiles"
                meta="Platform → tenant → merchant → store: each inherits the rules above it and may dial its appetite within the range its parent allows."
                primaryAction={
                    !platformHost && can(P.riskProfilesManage) ? (
                        <Button onClick={() => setEditing('new')}>
                            New profile
                        </Button>
                    ) : undefined
                }
            />
            <div className="space-y-4" data-testid="risk-profiles">
                {ordered.map((p) => (
                    <ProfileCard
                        key={p.id}
                        profile={p}
                        all={all}
                        onEdit={() => setEditing(p)}
                        onPublish={() => setPublishing(p)}
                    />
                ))}
                {tree.data && all.length === 0 ? (
                    <p className="text-fg-2 text-sm">
                        No profile yet: the platform seed has not been imported.
                    </p>
                ) : null}
            </div>
            {editing !== null ? (
                <ProfileSheet
                    profile={editing === 'new' ? null : editing}
                    parent={
                        editing === 'new'
                            ? (all.find((p) => p.scope === 'tenant') ??
                              all.find((p) => p.scope === 'platform') ??
                              null)
                            : (all.find((p) => p.id === editing.parent_id) ??
                              null)
                    }
                    onClose={() => setEditing(null)}
                />
            ) : null}
            {publishing ? (
                <PublishDialog
                    profile={publishing}
                    onClose={() => setPublishing(null)}
                />
            ) : null}
        </div>
    );
}
